<script setup lang="ts">
// ================================
// « Hypothèses de l'étude » : l'entrée du parcours guidé (DEV-2311, piste F
// validée le 07/10/2026, maquette allégée ; vague wording du 14/10/2026, Q8 :
// « Hypothèses de l'étude » pour lire, « Modifier les hypothèses » pour
// corriger, « Réglages d'expert (facultatif) » replié dedans).
//
// Ce que le client doit vivre :
// - sans étude pour ce compteur, il lit les paramètres recommandés (période
//   par défaut, prudence équilibrée, contrat lu chez Enedis, la meilleure
//   structure cherchée) et lance l'étude d'un seul bouton ; ou bien, avant ce
//   premier lancement, « Modifier les paramètres » ouvre « Ajuster l'étude »,
//   pré-rempli sur ces valeurs, dont le bouton lance la première étude avec
//   ses modifications (Q0, revue du 08/10/2026) ;
// - avec une étude, le bloc est replié sur sa ligne de valeurs ; il se
//   déroule sur « Ce qui a servi au calcul » et « Ajuster l'étude »,
//   repliés, et « Modifier les paramètres » ouvre directement l'ajustement ;
// - sur un refus, le bloc dit ce qui bloque (le refus servi, emplacement
//   `refus`) et ne montre que les paramètres qui le lèvent ; le reste est
//   derrière « Modifier les paramètres » ;
// - sans étude, sur un compteur dont la collecte n'est pas encore active (le
//   consentement du titulaire n'est pas signé), il lit pourquoi et ce qui la
//   rend active, avec le lien vers la fiche du compteur, et aucun bouton de
//   lancement : l'étude ne trouverait aucune donnée.
//
// Ouvrir le bloc est un geste vers un recalcul : il n'est gardé nulle part.
// Le contenu replié reste dans le document (v-show) : un brouillon en cours
// n'est jamais démonté.
// ================================
import {
    NIVEAU_1,
    NIVEAU_2,
} from "@modules/courtage/utils/optim-turpe-v2.boutons"
import { computed } from "vue"
import type { RouteLocationRaw } from "vue-router"
import OptimTurpeV2Section from "./OptimTurpeV2Section.vue"
import {
    ligneDuCadre,
    type Entete,
} from "@modules/courtage/utils/optim-turpe-v2.entetes"
import { phraseInjection } from "@modules/courtage/utils/optim-turpe-v2.helpers"
import { constatsDesMesures } from "@modules/courtage/utils/optim-turpe-v2.points"
import {
    CODE_RESERVE_COLLECTE_INTERROMPUE,
    noteDernierJourMesure,
} from "@modules/courtage/utils/optim-turpe-v2.cadre"
import {
    estLeDecompteDesJours,
    ligneDesMesures,
} from "@modules/courtage/utils/optim-turpe-v2.qualite"
import {
    LIBELLE_MARGE_C5,
    libelleMarge,
    MARGE_PCT_EQUILIBRE,
} from "@modules/courtage/utils/optim-turpe-v2.marge"
import type { OptimTurpeV2Etude } from "@modules/courtage/types/optim-turpe-v2"

type Mode = "etude" | "entree" | "refus"

type Props = {
    etude: OptimTurpeV2Etude | null
    ouvert: boolean
    /** `entree` : aucune étude encore ; `refus` : le dernier lancement a été refusé. */
    mode?: Mode
    /**
     * Un brouillon non relancé (lot B) : « 1 modification, non relancée », et
     * ce qu'il change. Il se lit sur la ligne, même le bloc replié.
     */
    attente?: { etiquette: string; note: string } | null
    /** L'en-tête de « Ce qui a servi au calcul » (noyau chiffré). */
    enteteCalcul?: Entete | null
    /** Les ouvertures des deux lignes de rang 2, tenues par la page. */
    details?: Record<string, boolean>
    /** Sur un refus : tous les paramètres sont-ils montrés ? */
    tousLesParametres?: boolean
    /** Le lancement de l'étude recommandée est-il possible (mode `entree`) ? */
    lancable?: boolean
    /**
     * Mode `entree` : la collecte du compteur n'est pas encore active (demande
     * de consentement non signée) ; le bloc le dit au lieu de proposer le
     * lancement.
     */
    consentementEnAttente?: boolean
    /**
     * Mode `entree` (reco (p), maquette jugée du 20/10/2026) : le moteur
     * refuserait l'étude faute des informations du contrat ; la carte dit ses
     * mots à la place de « Lancer l'étude ». Contrairement à la carte du
     * consentement, « Modifier les hypothèses » RESTE (R-T20) : une formule ou
     * un segment saisis rendent le lancement possible.
     */
    attenteDonnees?: { message: string; remede: string } | null
    /**
     * Mode `entree` : l'ouverture du compteur (liste de ses études, puis
     * l'étude à rouvrir) est en cours. « Lancer l'étude » attend sa fin : il
     * ne clignote plus avant l'étude ou le refus qui le remplacent (mesuré en
     * prod le 20/10/2026 : 457 à 1 259 ms à l'écran).
     */
    enOuverture?: boolean
    /** La fiche du compteur (page des compteurs du bâtiment), si la route existe. */
    lienCompteur?: RouteLocationRaw | null
    /**
     * Le segment du compteur sélectionné, seule valeur connue avant le premier
     * lancement : en C5, la marge de sécurité est de 15 % d'office (Q10).
     */
    segment?: string | null
}
const props = withDefaults(defineProps<Props>(), {
    mode: "etude",
    attente: null,
    enteteCalcul: null,
    details: () => ({}),
    tousLesParametres: false,
    lancable: true,
    consentementEnAttente: false,
    attenteDonnees: null,
    enOuverture: false,
    lienCompteur: null,
    segment: null,
})
const emit = defineEmits<{
    "update:ouvert": [ouvert: boolean]
    /** « Modifier les hypothèses » : ouvrir l'ajustement (ou tout montrer, sur un refus). */
    modifier: []
    detail: [id: string, ouvert: boolean]
    /** « Lancer l'étude » (mode `entree`). */
    lancer: []
}>()

const getLigne = computed(() => ligneDuCadre(props.etude))

/**
 * La ligne « Mesures » (contrat v1.2 §6 5.2, C9, D20 a) : les jours mesurés et
 * le pas des relevés, puis les constats de qualité servis (mentions courtes),
 * puis l'information « le site dépasse déjà sa puissance », sans étiquette.
 * Toujours en gris : la qualité des mesures n'est jamais une alerte.
 */
const getLigneDesMesures = computed(() => {
    const etude = props.etude
    if (!etude) return null
    const arret = getLigne.value.find((e) => e.mesures)?.mesures?.texte ?? null
    // L'arrêt de la collecte est écrit par la ligne, daté au moment de
    // l'étude : le libellé de la même réserve ne s'y ajoute pas (zéro redite).
    const { mentions, information } = constatsDesMesures(etude, {
        dejaDits:
            noteDernierJourMesure(etude)?.arretDepuis != null
                ? [CODE_RESERVE_COLLECTE_INTERROMPUE]
                : [],
    })
    const mesures = ligneDesMesures(etude)
    // Rejeu du 20/10/2026 (A13) : la mention servie qui redit le décompte
    // des jours (même champ `qualite_donnees`, autre arrondi du trou) ne
    // s'ajoute pas à la ligne qui l'écrit déjà, avec le pas des relevés et
    // les jours reconstitués. Une seule forme, celle qui dit le trou en
    // heures sous un jour, comme le moteur le compte (pas de dix minutes).
    const autres = mesures
        ? mentions.filter((m) => !estLeDecompteDesJours(m))
        : mentions
    const valeur = [mesures, arret, ...autres].filter(Boolean).join(" · ")
    if (!valeur && !information) return null
    // Maquette 2d (H2) : l'information suit les mentions, sans étiquette.
    const suite = information
        ? `${information.charAt(0).toLowerCase()}${information.slice(1).replace(/\.$/, "")}`
        : null
    return {
        label: "Mesures",
        value: [valeur || null, suite].filter(Boolean).join(" · "),
    }
})

/**
 * Les hypothèses de l'étude en lignes uniques « libellé / valeur »
 * (BaseSummary) : Période, Mesures, Contrat d'acheminement, Marge de
 * sécurité, Objectif, Grille TURPE ; chaque valeur avec sa source (D6 a).
 */
const getLignesResume = computed(() => {
    const lignes = getLigne.value
        .filter((element) => element.terme !== "Mesures")
        .map((element) => ({
            label: element.terme,
            value: [element.valeur, element.note].filter(Boolean).join(" · "),
        }))
    const mesures = getLigneDesMesures.value
    if (mesures) {
        const rang = lignes.findIndex((l) => l.label === "Période")
        lignes.splice(rang + 1, 0, mesures)
    }
    return lignes
})

/** Le fait d'injection de CE compteur (DEV-2158), seulement quand il appelle une phrase. */
const getInjection = computed(() =>
    props.etude ? phraseInjection(props.etude) : null
)

/**
 * Les hypothèses de l'étude, dites avant son lancement (W4, L1 ; Q10 : en C5,
 * la marge de 15 % est appliquée d'office).
 */
const getHypotheses = computed(() => [
    { terme: "Période", valeur: "les 12 derniers mois mesurés" },
    { terme: "Contrat d'acheminement", valeur: "lu chez Enedis au lancement" },
    {
        terme: "Marge de sécurité",
        valeur:
            props.segment === "C5"
                ? LIBELLE_MARGE_C5
                : libelleMarge(MARGE_PCT_EQUILIBRE),
    },
    { terme: "Objectif", valeur: "la combinaison la moins chère" },
])

/** Les quatre hypothèses en lignes « libellé / valeur » (BaseSummary 2 × 2). */
const getHypothesesResume = computed(() =>
    getHypotheses.value.map((element) => ({
        label: element.terme,
        value: element.valeur,
    }))
)

const ENTETE_AJUSTER: Entete = {
    forme: "repliee",
    libelle: "Modifier les hypothèses",
    noyau: "période, marge de sécurité, contrat d'acheminement",
    complement: null,
    pastille: null,
    pastilleReserve: false,
}
const getEnteteCalcul = computed<Entete>(
    () =>
        props.enteteCalcul ?? {
            forme: "absente",
            libelle: "Valeurs utilisées",
            noyau: null,
            complement: null,
            pastille: null,
            pastilleReserve: false,
        }
)

/**
 * Q0 : sans étude, le formulaire est-il ouvert ? Tant qu'il l'est, son pied
 * porte le lancement (un seul bouton de lancement à l'écran).
 */
const getFormulaireOuvert = computed(
    () =>
        props.mode === "entree" &&
        props.ouvert &&
        props.details.ajuster === true
)

const getLibelleModifier = computed(() => {
    if (props.mode === "refus") {
        return props.tousLesParametres
            ? "Ne montrer que ce qui bloque"
            : "Afficher toutes les hypothèses"
    }
    return props.ouvert && props.details.ajuster === true
        ? "Replier les hypothèses"
        : "Modifier les hypothèses"
})
</script>

<template>
    <!-- Maquette finale du 15/10/2026 : avec une étude, le bloc est le
         contenu de la rangée « Hypothèses de l'étude » du sommaire ; le titre
         est celui de la rangée. Sans étude et sur un refus, rendu inchangé. -->
    <section
        :id="mode === 'etude' ? undefined : 'm-cadre'"
        class="optim-turpe-v2-cadre flex scroll-mt-36 flex-col"
        :class="
            mode === 'etude'
                ? 'gap-4'
                : 'gap-3 rounded-lg border border-surface-200 bg-white px-5 py-3'
        "
        :aria-labelledby="mode === 'etude' ? undefined : 'm-cadre-h'"
        :data-mode="mode"
        data-testid="optim-turpe-v2-cadre"
    >
        <div
            v-if="mode !== 'etude'"
            class="flex flex-wrap items-start justify-between gap-x-4 gap-y-2"
        >
            <h2 id="m-cadre-h" class="text-base font-semibold text-primary-900">
                Hypothèses de l'étude
            </h2>
            <BaseButton
                v-if="mode !== 'entree' || !consentementEnAttente"
                v-bind="NIVEAU_2"
                :label="getLibelleModifier"
                :aria-expanded="mode === 'refus' ? tousLesParametres : ouvert"
                aria-controls="m-cadre-corps"
                data-testid="optim-turpe-v2-cadre-bascule"
                @click="emit('modifier')"
            />
        </div>

        <!-- SANS ÉTUDE, COLLECTE PAS ENCORE ACTIVE : ce qui la rend active,
             et aucun bouton (l'étude ne trouverait aucune donnée). -->
        <template v-if="mode === 'entree' && consentementEnAttente">
            <p
                class="text-sm text-primary-700"
                data-testid="optim-turpe-v2-consentement-en-attente"
            >
                La collecte de ce compteur n'est pas encore active : le
                consentement du titulaire n'a pas été signé. L'étude pourra être
                lancée dès que la collecte sera active.
            </p>
            <RouterLink
                v-if="lienCompteur"
                :to="lienCompteur"
                class="w-fit text-sm font-medium text-primary-600 underline"
                data-testid="optim-turpe-v2-lien-fiche-compteur"
            >
                Ouvrir la fiche du compteur
            </RouterLink>
            <slot name="entree" />
        </template>

        <!-- SANS ÉTUDE, DONNÉES DU CONTRAT NON REÇUES (reco (p)) : les mots
             du refus que le moteur servirait, le lien vers la fiche, aucun
             « Lancer l'étude » ; le formulaire reste, son pied lance. -->
        <template v-else-if="mode === 'entree' && attenteDonnees">
            <BaseMessage
                severity="info"
                data-testid="optim-turpe-v2-attente-donnees"
            >
                <p
                    class="text-sm font-semibold text-primary-900"
                    data-testid="optim-turpe-v2-attente-donnees-message"
                >
                    {{ attenteDonnees.message }}
                </p>
                <p
                    class="mt-2 text-sm text-primary-800"
                    data-testid="optim-turpe-v2-attente-donnees-remede"
                >
                    {{ attenteDonnees.remede }}
                </p>
            </BaseMessage>
            <RouterLink
                v-if="lienCompteur"
                :to="lienCompteur"
                class="w-fit text-sm font-medium text-primary-800 underline"
                data-testid="optim-turpe-v2-lien-fiche-compteur"
            >
                Ouvrir la fiche du compteur
            </RouterLink>
            <slot name="entree" />
            <div
                v-show="getFormulaireOuvert"
                id="m-cadre-corps"
                class="flex flex-col gap-4 border-t border-surface-100 pt-3"
                data-testid="optim-turpe-v2-cadre-corps"
            >
                <slot />
            </div>
        </template>

        <!-- SANS ÉTUDE : ce que fait l'étude (L10), ses hypothèses, et un
             seul bouton. -->
        <template v-else-if="mode === 'entree'">
            <!-- Revue 2 en production (I63, I66) : l'explication en deux
                 lignes au plus, les quatre hypothèses en 2 × 2, et la seule
                 action de l'écran en grand, pleine largeur sous 640 px. -->
            <p
                class="text-sm text-surface-700"
                data-testid="optim-turpe-v2-accroche"
            >
                L'étude compare les formules d'acheminement (FTA) et les
                puissances possibles à partir des mesures du compteur, et dit si
                une autre combinaison coûterait moins cher.
            </p>
            <BaseSummary
                :lines="getHypothesesResume"
                variant="grey"
                class="grid! grid-cols-1 gap-x-8 sm:grid-cols-2"
                data-testid="optim-turpe-v2-parametres-recommandes"
            />
            <slot name="entree" />
            <BaseButton
                v-if="!getFormulaireOuvert && !enOuverture"
                v-bind="NIVEAU_1"
                label="Lancer l'étude"
                icon="pi pi-play"
                size="large"
                class="w-full sm:w-fit"
                :disabled="!lancable"
                data-testid="optim-turpe-v2-lancer"
                @click="emit('lancer')"
            />
            <!-- Q0 : « Modifier les hypothèses » avant le premier lancement ; le
                 brouillon n'est jamais démonté par un repli. -->
            <div
                v-show="getFormulaireOuvert"
                id="m-cadre-corps"
                class="flex flex-col gap-4 border-t border-surface-100 pt-3"
                data-testid="optim-turpe-v2-cadre-corps"
            >
                <slot />
            </div>
        </template>

        <!-- UN REFUS : ce qui bloque, puis les seuls paramètres qui le lèvent. -->
        <template v-else-if="mode === 'refus'">
            <slot name="refus" />
            <div
                id="m-cadre-corps"
                class="flex flex-col gap-4"
                data-testid="optim-turpe-v2-cadre-corps"
            >
                <slot />
            </div>
        </template>

        <!-- UNE ÉTUDE : les valeurs et leur provenance (BaseSummary), le
             geste « Modifier les hypothèses », puis le corps replié. -->
        <template v-else>
            <BaseSummary
                v-if="getLignesResume.length > 0"
                :lines="getLignesResume"
                variant="grey"
                data-testid="optim-turpe-v2-cadre-ligne"
            />
            <p
                v-if="attente"
                class="flex flex-wrap items-center gap-2 text-sm text-surface-700"
                data-testid="optim-turpe-v2-cadre-attente"
            >
                <BaseTag severity="warn" :value="attente.etiquette" />
                <span>{{ attente.note }}</span>
            </p>
            <p
                v-if="getInjection"
                class="text-sm text-surface-700"
                data-testid="optim-turpe-v2-injection"
            >
                {{ getInjection }}
            </p>
            <!-- Revue 2 en production (I43) : une seule entrée « Modifier les
                 hypothèses », la sous-rangée ; plus de bouton qui la
                 redouble. -->
            <div
                id="m-cadre-corps"
                class="flex flex-col"
                data-testid="optim-turpe-v2-cadre-corps"
            >
                <OptimTurpeV2Section
                    id="acc-contrat"
                    :entete="getEnteteCalcul"
                    :rang="2"
                    :ouvert="details.contrat === true"
                    @update:ouvert="emit('detail', 'contrat', $event)"
                >
                    <slot name="calcul" />
                </OptimTurpeV2Section>
                <OptimTurpeV2Section
                    id="acc-ajuster"
                    :entete="ENTETE_AJUSTER"
                    :rang="2"
                    :ouvert="details.ajuster === true"
                    @update:ouvert="emit('detail', 'ajuster', $event)"
                >
                    <slot />
                </OptimTurpeV2Section>
            </div>
        </template>
    </section>
</template>
